import React, { useState, useEffect } from 'react'
import { Search, Check, X, Loader2 } from 'lucide-react'
import { dataInputsService } from '../services/dataInputs'
import toast from 'react-hot-toast'

const MultiSelectFilter = ({ 
  dataInputId, 
  attribute, 
  selectedValues = [], 
  onSelectionChange, 
  onClose 
}) => {
  const [uniqueValues, setUniqueValues] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [localSelectedValues, setLocalSelectedValues] = useState(new Set(selectedValues))

  // Fetch unique values when component mounts
  useEffect(() => {
    const fetchUniqueValues = async () => {
      try {
        setLoading(true)
        const response = await dataInputsService.getColumnUniqueValues(dataInputId, attribute.name, 200)
        setUniqueValues(response.unique_values || [])
      } catch (error) {
        console.error('Error fetching unique values:', error)
        toast.error('Failed to load filter options')
      } finally {
        setLoading(false)
      }
    }

    fetchUniqueValues()
  }, [dataInputId, attribute.name])

  // Filter values based on search term
  const filteredValues = uniqueValues.filter(value =>
    value.toLowerCase().includes(searchTerm.toLowerCase())
  )

  // Handle individual value selection
  const handleValueToggle = (value) => {
    const newSelected = new Set(localSelectedValues)
    if (newSelected.has(value)) {
      newSelected.delete(value)
    } else {
      newSelected.add(value)
    }
    setLocalSelectedValues(newSelected)
  }

  // Handle select all
  const handleSelectAll = () => {
    setLocalSelectedValues(new Set(filteredValues))
  }

  // Handle deselect all
  const handleDeselectAll = () => {
    setLocalSelectedValues(new Set())
  }

  // Apply selection
  const handleApply = () => {
    const selectedArray = Array.from(localSelectedValues)
    onSelectionChange(selectedArray)
    onClose()
  }

  // Cancel selection
  const handleCancel = () => {
    setLocalSelectedValues(new Set(selectedValues))
    onClose()
  }

  if (loading) {
    return (
      <div className="absolute top-full left-0 mt-1 w-80 bg-white border border-gray-200 rounded-lg shadow-lg z-50 p-4">
        <div className="flex items-center justify-center">
          <Loader2 className="h-4 w-4 animate-spin mr-2" />
          <span className="text-sm text-gray-600">Loading options...</span>
        </div>
      </div>
    )
  }

  return (
    <div className="absolute top-full left-0 mt-1 w-80 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
      {/* Header */}
      <div className="p-3 border-b border-gray-200">
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-sm font-medium text-gray-900">
            Filter {attribute.label || attribute.name}
          </h4>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-3 w-3 text-gray-400" />
          <input
            type="text"
            placeholder="Search values..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-7 pr-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-500"
          />
        </div>
      </div>

      {/* Select All / Deselect All */}
      <div className="p-2 border-b border-gray-200 bg-gray-50">
        <div className="flex space-x-2">
          <button
            onClick={handleSelectAll}
            className="text-xs text-primary-600 hover:text-primary-800"
            disabled={filteredValues.length === 0}
          >
            Select All ({filteredValues.length})
          </button>
          <span className="text-xs text-gray-400">|</span>
          <button
            onClick={handleDeselectAll}
            className="text-xs text-gray-600 hover:text-gray-800"
          >
            Deselect All
          </button>
        </div>
      </div>

      {/* Values List */}
      <div className="max-h-60 overflow-y-auto">
        {filteredValues.length === 0 ? (
          <div className="p-3 text-center text-sm text-gray-500">
            {searchTerm ? 'No matching values found' : 'No values available'}
          </div>
        ) : (
          <div className="p-2">
            {filteredValues.map((value, index) => (
              <label
                key={index}
                className="flex items-center space-x-2 p-1 hover:bg-gray-50 rounded cursor-pointer"
              >
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={localSelectedValues.has(value)}
                    onChange={() => handleValueToggle(value)}
                    className="sr-only"
                  />
                  <div className={`w-4 h-4 border rounded flex items-center justify-center ${
                    localSelectedValues.has(value)
                      ? 'bg-primary-600 border-primary-600 text-white'
                      : 'border-gray-300'
                  }`}>
                    {localSelectedValues.has(value) && (
                      <Check className="h-3 w-3" />
                    )}
                  </div>
                </div>
                <span className="text-xs text-gray-700 truncate flex-1" title={value}>
                  {value}
                </span>
              </label>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="p-3 border-t border-gray-200 bg-gray-50">
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-600">
            {localSelectedValues.size} selected
          </span>
          <div className="flex space-x-2">
            <button
              onClick={handleCancel}
              className="px-3 py-1 text-xs text-gray-600 hover:text-gray-800"
            >
              Cancel
            </button>
            <button
              onClick={handleApply}
              className="px-3 py-1 text-xs bg-primary-600 text-white rounded hover:bg-primary-700"
            >
              Apply
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default MultiSelectFilter
